import URL2 from '../config.js';
import { getChildCampusName } from './parentHistoryHelpers.js';
import {
    getCampusSessions,
    getPaymentResumeState,
} from './campusPaymentCharges.js';
import {
    extractCampusRows,
    hasOutstandingCampusPayment,
} from './campusPaymentStatus.js';

/**
 * Rebuilds the nav state `/payment` needs so a family with a part-paid
 * multi-campus re-enrolment can be dropped straight onto the fee page instead
 * of walking the whole form again.
 */

const sessionKey = (campus) => String(campus?.SessionID ?? '');

export const mergeCampusSessions = (...lists) => {
    const merged = new Map();

    lists.forEach((list) => {
        (Array.isArray(list) ? list : []).forEach((campus) => {
            if (!campus || !sessionKey(campus)) return;
            const key = sessionKey(campus);
            merged.set(key, {
                ...(merged.get(key) || {}),
                SessionID: campus.SessionID,
                college: campus.college ?? merged.get(key)?.college,
                parent_code: campus.parent_code ?? merged.get(key)?.parent_code,
            });
        });
    });

    return Array.from(merged.values());
};

/**
 * Returns null when there is not enough stored context to price the remaining
 * campuses — `/calculatefees` cannot run without the enrolled children, so in
 * that case the caller must fall back to the normal flow.
 */
export const buildPaymentResumeNavState = ({ statusData, ParentID, SessionID, college, credit_amount } = {}) => {
    const stored = getPaymentResumeState();
    const enrolledchildrens = stored.enrolledchildrens || stored.childrens;

    if (!Array.isArray(enrolledchildrens) || enrolledchildrens.length === 0) return null;

    const resumeSessionID = stored.SessionID || SessionID;
    if (!resumeSessionID) return null;

    const campusSessions = mergeCampusSessions(
        stored.campusSessions,
        getCampusSessions(),
        extractCampusRows(statusData)
    );

    return {
        ...stored,
        SessionID: resumeSessionID,
        enrolledchildrens,
        childrens: stored.childrens || enrolledchildrens,
        ParentID: stored.ParentID ?? ParentID,
        parentID: stored.parentID ?? ParentID,
        college: stored.college || college,
        credit_amount: stored.credit_amount ?? credit_amount,
        campusSessions,
        resumingCampusPayment: true,
    };
};

/**
 * The single decision point: given a `/checkStatus` or `/parent-login`
 * response, the nav state to resume payment with, or null to carry on as usual.
 */
export const resolveCampusPaymentResume = (statusData, overrides = {}) => {
    if (!hasOutstandingCampusPayment(statusData)) return null;
    return buildPaymentResumeNavState({ statusData, ...overrides });
};

/**
 * The payment page's context from the server, for a family with a campus paid and another to pay
 * (GET /api/payment-context). { context } to resume at payment, { allPaid } when nothing is left,
 * {} for anything else — the caller then walks the flow as it always has.
 */
export const paymentContextOnServer = async (parentId) => {
    try {
        const { data } = await URL2.get('/payment-context', { params: { ParentID: parentId } });
        return data && data.SessionID ? { context: data } : {};
    } catch (error) {
        if (error?.response?.status === 409 && error?.response?.data?.allPaid) return { allPaid: true };
        return {};
    }
};

const same = (a, b) => String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();

/** The paid campus a child belongs to, by its parent code, then by campus name; or null. */
export const paidCampusForChild = (child = {}, paidCampuses = []) => {
    const paid = (paidCampuses || []).filter((campus) => campus && campus.paid !== false);
    return paid.find((campus) => child.parent_code && same(campus.parent_code, child.parent_code))
        || paid.find((campus) => getChildCampusName(child) && same(campus.college, getChildCampusName(child)))
        || null;
};
